import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import VarotradesSplashLoader from "./components/VarotradesSplashLoader";
import AppErrorBoundary from "./components/AppErrorBoundary";

// Lazy load pages - only loads when user navigates to them
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const TradingPlatform = lazy(() => import("./pages/TradingPlatform"));
const SSOLanding = lazy(() => import("./pages/SSOLanding"));
const SuperAdminPanel = lazy(() => import("./pages/SuperAdminPanel"));

// Premium loading splash — 3D orbiting candles around VAROTRADES logo
const PageLoader = () => <VarotradesSplashLoader />;

// Protected Route - requires authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route - redirect authenticated users to platform (or super-admin)
const PublicRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    const target = user?.role === "super_admin" ? "/super-admin" : "/platform";
    return <Navigate to={target} replace />;
  }

  return children;
};

// Super-admin guard
const SuperAdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== "super_admin") return <Navigate to="/platform" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/sso" element={<SSOLanding />} />
        
        {/* Protected Routes */}
        <Route path="/platform" element={<ProtectedRoute><TradingPlatform /></ProtectedRoute>} />
        <Route path="/trade" element={<ProtectedRoute><TradingPlatform /></ProtectedRoute>} />
        <Route path="/dashboard" element={<Navigate to="/platform" replace />} />

        {/* Super Admin Panel */}
        <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminPanel /></SuperAdminRoute>} />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        
        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App overflow-x-hidden w-full max-w-full">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppErrorBoundary>
              <AppRoutes />
            </AppErrorBoundary>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
